/**
 * Admin → IP Intel Report → Coupons: codes that take a percent or a fixed
 * amount off the report's price before tax (Backend ip_intel.py: coupons).
 * Buyers type them in the order sheet (platform, public tech pages, the IP
 * Intel page); the order re-checks the code and prices itself. "Uses" counts
 * PAID orders, the number a use limit is checked against.
 */
import React, { useState } from 'react';
import { Btn, DataTable, Msg, Section, panelPost, useAdminGet } from '../kit';
import { S } from './shared';

interface Coupon {
  code: string; kind: 'percent' | 'amount'; label: string; active: boolean;
  percent_off: number | null; amount_off_minor: number | null; amount_off: number | null; currency: string | null;
  valid_from: string | null; valid_until: string | null; max_uses: number | null; uses: number;
  note: string; created_by: string; created_at: string | null;
}

const blank = { code: '', kind: 'percent' as 'percent' | 'amount', percent_off: '10', amount: '', currency: '',
  valid_from: '', valid_until: '', max_uses: '', note: '' };
const day = (iso: string | null) => (iso ? iso.slice(0, 10) : '');

const Coupons: React.FC = () => {
  const res = useAdminGet<{ coupons: Coupon[]; currencies: string[] }>(S, 'coupons');
  const [form, setForm] = useState({ ...blank });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const coupons = res.data?.coupons || [];
  const currencies = res.data?.currencies || [];
  const set = (k: keyof typeof blank, v: string) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true); setMsg(null);
    const body: Record<string, unknown> = {
      code: form.code.trim().toUpperCase(), kind: form.kind, note: form.note.trim(),
      valid_from: form.valid_from || null, valid_until: form.valid_until || null,
      // No `active`: a new code starts on, an edited one keeps its state
      // (Switch on / off is its own button).
      max_uses: form.max_uses.trim() ? Number(form.max_uses) : null,
    };
    if (form.kind === 'percent') body.percent_off = Number(form.percent_off);
    else { body.amount = Number(form.amount); body.currency = form.currency || currencies[0]; }
    const { error } = await panelPost(S, 'coupon_save', body);
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setMsg({ kind: 'ok', text: `Saved ${body.code}. Buyers can use it straight away.` });
    setForm({ ...blank });
    res.reload();
  };

  const toggle = async (c: Coupon) => {
    const { error } = await panelPost(S, 'coupon_toggle', { code: c.code, active: !c.active });
    if (error) setMsg({ kind: 'error', text: error }); else res.reload();
  };

  const edit = (c: Coupon) => setForm({
    code: c.code, kind: c.kind, percent_off: c.percent_off != null ? String(c.percent_off) : '10',
    amount: c.amount_off != null ? String(c.amount_off) : '', currency: c.currency || '',
    valid_from: day(c.valid_from), valid_until: day(c.valid_until),
    max_uses: c.max_uses != null ? String(c.max_uses) : '', note: c.note || '',
  });

  const input = 'h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm';
  return (
    <div className="flex flex-col gap-4">
      <Section title="New or edit a code">
        {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
        <p className="text-xs text-grayscale-1100 -mt-1 max-w-3xl">
          Taken off the base price before tax. Saving an existing code replaces its terms (its on/off state is
          kept). Dates are whole days, India time. A code cannot bring the total below one unit (percent codes stay
          under 100%). The use limit counts paid orders plus checkouts opened with the code in the last 30 minutes.
        </p>
        <div className="flex flex-wrap items-end gap-3 text-xs text-grayscale-1100">
          <label className="flex flex-col gap-1">Code
            <input className={`${input} w-40 uppercase`} value={form.code} maxLength={32} placeholder="LAUNCH20"
              onChange={e => set('code', e.target.value.toUpperCase())} />
          </label>
          <label className="flex flex-col gap-1">Type
            <select className={input} value={form.kind} onChange={e => set('kind', e.target.value)}>
              <option value="percent">Percent off</option>
              <option value="amount">Fixed amount off</option>
            </select>
          </label>
          {form.kind === 'percent' ? (
            <label className="flex flex-col gap-1">Percent
              <input className={`${input} w-24`} type="number" min={1} max={99} value={form.percent_off}
                onChange={e => set('percent_off', e.target.value)} />
            </label>
          ) : (
            <>
              <label className="flex flex-col gap-1">Amount
                <input className={`${input} w-28`} type="number" min={1} value={form.amount} placeholder="5000"
                  onChange={e => set('amount', e.target.value)} />
              </label>
              <label className="flex flex-col gap-1">Currency
                <select className={input} value={form.currency || currencies[0] || ''} onChange={e => set('currency', e.target.value)}>
                  {currencies.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            </>
          )}
          <label className="flex flex-col gap-1">Valid from
            <input className={input} type="date" value={form.valid_from} onChange={e => set('valid_from', e.target.value)} />
          </label>
          <label className="flex flex-col gap-1">Valid until
            <input className={input} type="date" value={form.valid_until} onChange={e => set('valid_until', e.target.value)} />
          </label>
          <label className="flex flex-col gap-1">Use limit
            <input className={`${input} w-24`} type="number" min={1} value={form.max_uses} placeholder="none"
              onChange={e => set('max_uses', e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 flex-1 min-w-[180px]">Note (internal)
            <input className={input} value={form.note} maxLength={300} onChange={e => set('note', e.target.value)} />
          </label>
          <Btn kind="primary" onClick={save} disabled={busy || !form.code.trim()}>{busy ? 'Saving…' : 'Save code'}</Btn>
        </div>
      </Section>

      <Section title={`Codes (${coupons.length})`}>
        {res.error && <Msg kind="error">{res.error}</Msg>}
        <DataTable
          rows={coupons.map(c => ({
            Code: c.code, Discount: c.label, Status: c.active ? 'Active' : 'Off',
            'Valid': [day(c.valid_from) || '—', day(c.valid_until) || '—'].join(' → '),
            'Paid uses': c.max_uses != null ? `${c.uses} / ${c.max_uses}` : String(c.uses),
            Note: c.note, _c: c,
          }))}
          columns={['Code', 'Discount', 'Status', 'Valid', 'Paid uses', 'Note']}
          actions={row => (
            <span className="inline-flex items-center gap-2">
              <Btn small onClick={() => toggle(row._c)}>{row._c.active ? 'Switch off' : 'Switch on'}</Btn>
              <Btn small onClick={() => edit(row._c)}>Edit</Btn>
            </span>
          )}
          empty="No codes yet."
        />
      </Section>
    </div>
  );
};

export default Coupons;
